import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, ArrowLeft, Check, Smartphone, Share, MoreVertical, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const DownloadPage = () => {
  const navigate = useNavigate();
  const deferredPromptRef = useRef(null);
  const [platform, setPlatform] = useState('unknown');
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [canInstallNatively, setCanInstallNatively] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid || /mobile/.test(userAgent);
    
    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    } else if (isMobile) {
      setPlatform('mobile');
    } else {
      setPlatform('desktop');
    }
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
    }
    
    // Check if global prompt was already captured
    if (window.__pwaInstallPrompt) {
      deferredPromptRef.current = window.__pwaInstallPrompt;
      setCanInstallNatively(true);
    }

    // Listen for beforeinstallprompt (in case it fires after mount)
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      window.__pwaInstallPrompt = e;
      setCanInstallNatively(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for app installed
    const installedHandler = () => {
      setIsInstalled(true);
      toast.success('Aplikacja zainstalowana!');
    };
    window.addEventListener('appinstalled', installedHandler);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstallClick = async () => {
    // If native prompt available, use it immediately
    if (deferredPromptRef.current) {
      try {
        deferredPromptRef.current.prompt();
        const { outcome } = await deferredPromptRef.current.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
          toast.success('Aplikacja zainstalowana!');
        }
        deferredPromptRef.current = null;
        setCanInstallNatively(false);
      } catch (err) {
        // Fallback to instructions
        setShowInstructions(true);
      }
    } else if (platform === 'ios') {
      // iOS - show instructions (no automatic install possible)
      setShowInstructions(true);
      toast.info('Na iPhone użyj przycisku "Udostępnij" w Safari');
    } else {
      // Android without prompt or other browser - show instructions
      setShowInstructions(true);
      toast.info('Użyj menu przeglądarki aby zainstalować');
    }
  };

  // Already installed view
  if (isInstalled) {
    return (
      <div className="min-h-screen px-6 py-8" style={{ background: 'transparent' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="mb-8 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" /> Wróć
        </button>
        
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Gotowe!</h1>
          <p className="text-slate-400 mb-8">
            Aplikacja PromptOrigin jest zainstalowana.<br />
            Znajdziesz ją na ekranie głównym.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="h-12 px-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            Otwórz aplikację
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-8 pb-24" style={{ background: 'transparent' }}>
      <button 
        onClick={() => navigate(-1)} 
        className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-5 h-5" /> Wróć
      </button>
      
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center mb-4 mx-auto shadow-lg">
          <Download className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Zainstaluj PromptOrigin</h1>
        <p className="text-slate-400">Aplikacja działa jak natywna — bez App Store.</p>
      </div>

      {/* Refresh hint — shown when native prompt is not available */}
      {!canInstallNatively && platform !== 'ios' && (
        <div 
          className="mb-5 rounded-xl px-4 py-3 flex items-center gap-3"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)' }}
          data-testid="refresh-hint"
        >
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-amber-400 text-sm">↻</span>
          </div>
          <div>
            <p className="text-sm font-medium text-amber-300">Nie widzisz okna instalacji?</p>
            <p className="text-xs text-slate-400 mt-0.5">Odśwież stronę, a potem kliknij „Zainstaluj aplikację" ponownie.</p>
          </div>
        </div>
      )}

      {/* MAIN CTA BUTTON - ALWAYS VISIBLE */}
      <div className="mb-8">
        <Button
          onClick={handleInstallClick}
          className="w-full h-14 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-lg font-semibold shadow-lg shadow-sky-500/25 active:scale-[0.98] transition-transform"
          data-testid="install-btn"
        >
          <Download className="w-5 h-5 mr-2" />
          {canInstallNatively ? 'Zainstaluj aplikację' : 'Zainstaluj aplikację'}
        </Button>
        {!canInstallNatively && platform !== 'desktop' && (
          <p className="text-center text-xs text-slate-500 mt-2">
            {platform === 'ios' ? 'Użyj Safari i przycisku Udostępnij' : 'Otwórz menu przeglądarki'}
          </p>
        )}
        <p className="text-center text-xs text-slate-400 mt-3">
          Platforma działa również bez instalacji aplikacji.
        </p>
      </div>

      {/* Instructions Modal/Section - shown after clicking when no native prompt */}
      {showInstructions && (
        <Card 
          className="border-2 border-sky-500/30 shadow-lg mb-6 animate-in fade-in slide-in-from-top-4 duration-300" 
          style={{ background: 'rgba(14, 165, 233, 0.1)', backdropFilter: 'blur(12px)' }}
        >
          <CardContent className="pt-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-sky-400" />
              Instrukcja instalacji
            </h2>
            
            {platform === 'ios' ? (
              <IOSInstructions />
            ) : (
              <AndroidInstructions />
            )}

            <Button
              onClick={() => setShowInstructions(false)}
              variant="ghost"
              className="w-full mt-4 text-slate-400 hover:text-white"
            >
              Zamknij instrukcję
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Permanent instruction card - collapsed by default on mobile */}
      {!showInstructions && (
        <Card 
          className="border-0 shadow-md mb-6" 
          style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}
        >
          <CardContent className="pt-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-sky-400" />
              Jak zainstalować ręcznie?
            </h2>
            
            {platform === 'ios' ? (
              <IOSInstructions />
            ) : (
              <AndroidInstructions />
            )}
          </CardContent>
        </Card>
      )}

      {/* Alternative platform instructions */}
      {platform !== 'ios' && (
        <details className="mb-4">
          <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-300 py-2">
            Używasz iPhone'a? Zobacz instrukcję dla iOS →
          </summary>
          <Card className="mt-2 border-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <CardContent className="pt-4">
              <IOSInstructions compact />
            </CardContent>
          </Card>
        </details>
      )}

      {platform === 'ios' && (
        <details className="mb-4">
          <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-300 py-2">
            Używasz Androida? Zobacz instrukcję →
          </summary>
          <Card className="mt-2 border-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <CardContent className="pt-4">
              <AndroidInstructions compact />
            </CardContent>
          </Card>
        </details>
      )}
    </div>
  );
};

// iOS Installation Instructions Component
const IOSInstructions = ({ compact = false }) => {
  if (compact) {
    return (
      <ol className="space-y-2 text-sm text-slate-400">
        <li><strong className="text-white">1.</strong> Otwórz stronę w Safari</li>
        <li><strong className="text-white">2.</strong> Kliknij ikonę Udostępnij <Share className="w-3 h-3 inline text-sky-400" /></li>
        <li><strong className="text-white">3.</strong> Wybierz „Dodaj do ekranu początkowego"</li>
      </ol>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">1</span>
        </div>
        <div>
          <p className="font-medium text-white">Kliknij „Udostępnij"</p>
          <p className="text-sm text-slate-400 mt-1">
            Ikona <Share className="w-4 h-4 inline text-sky-400" /> na dole ekranu (Safari)
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">2</span>
        </div>
        <div>
          <p className="font-medium text-white">Wybierz „Dodaj do ekranu początkowego"</p>
          <p className="text-sm text-slate-400 mt-1">
            <Plus className="w-4 h-4 inline text-sky-400" /> Przewiń listę, jeśli nie widzisz
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">3</span>
        </div>
        <div>
          <p className="font-medium text-white">Kliknij „Dodaj"</p>
          <p className="text-sm text-slate-400 mt-1">Aplikacja pojawi się na ekranie głównym</p>
        </div>
      </div>
    </div>
  );
};

// Android Installation Instructions Component
const AndroidInstructions = ({ compact = false }) => {
  if (compact) {
    return (
      <ol className="space-y-2 text-sm text-slate-400">
        <li><strong className="text-white">1.</strong> Kliknij menu w Chrome <MoreVertical className="w-3 h-3 inline text-sky-400" /></li>
        <li><strong className="text-white">2.</strong> Wybierz „Zainstaluj aplikację"</li>
        <li><strong className="text-white">3.</strong> Potwierdź instalację</li>
      </ol>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">1</span>
        </div>
        <div>
          <p className="font-medium text-white">Otwórz menu przeglądarki</p>
          <p className="text-sm text-slate-400 mt-1">
            Kliknij <MoreVertical className="w-4 h-4 inline text-sky-400" /> w prawym górnym rogu
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">2</span>
        </div>
        <div>
          <p className="font-medium text-white">Wybierz „Zainstaluj aplikację"</p>
          <p className="text-sm text-slate-400 mt-1">Lub „Dodaj do ekranu głównego"</p>
        </div>
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sky-400 font-bold">3</span>
        </div>
        <div>
          <p className="font-medium text-white">Potwierdź instalację</p>
          <p className="text-sm text-slate-400 mt-1">Aplikacja pojawi się na ekranie głównym</p>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
